import { Heading, HStack, Text } from '@chakra-ui/react'
import { DateTime } from 'luxon'
import { useRouter } from 'next/router'
import { type ReactElement, useState } from 'react'

import type { SessionsSeriesData } from '@/common/services/seriesPage/types'
import parseQueryNumber from '@/utils/parseQueryNumber'

import { useServerNow } from '@/client/hooks'
import {
  DIALOGUES_REFLECTION_EXPIRES_AFTER_DAYS,
  QUERY_REFLECTION_SESSION_ID,
} from '@/client/services/dialogues/constants'
import { isDialogueSeries } from '@/client/services/dialogues/helpers'
import { EMPTY_RICH_TEXT_VALUE } from '@/client/services/richText/constants'
import { updateQueryParams } from '@/client/services/url/helpers'

import DialoguesReflectionModal from '@/components/Dialogues/DialoguesReflectionModal'
import ExcusedAbsenceModal from '@/components/Series/ExcusedAbsenceModal'
import Tabs from '@/components/Tabs/Tabs'
import useUrlTabController from '@/components/Tabs/useUrlTabController'

import SeriesAttendancePolicy from '../../SeriesAttendancePolicy'
import SessionCardList from './internal/SessionCardList'

type ScheduleTabs = 'Upcoming' | 'All' | 'Action Required'

export default function Sessions({
  series,
  isUserParticipant,
  isUserRegisteredLearner,
}: {
  series: SessionsSeriesData
  isUserParticipant: boolean
  isUserRegisteredLearner: boolean
}): ReactElement {
  const router = useRouter()
  const now = useServerNow()

  const querySessionId = router.query[QUERY_REFLECTION_SESSION_ID]
  const selectedSessionId =
    querySessionId != null ? parseQueryNumber({ value: querySessionId, defaultValue: 0 }) : null

  const seriesHasEnded = series.isClosed

  const seriesTemplateSlug = series.seriesTemplateSlug ?? ''
  const isDialogue = isDialogueSeries(seriesTemplateSlug)

  const allSessions = series.sessions.map(session => {
    const excusedAbsenceSource = session.viewer.excusedAbsenceSource
    const hasExcusedAbsence = excusedAbsenceSource != null
    const didAttemptSession = session.viewer.attemptedJoining
    const didOrWillAttend = session.hasEnded ? didAttemptSession : !hasExcusedAbsence

    return {
      id: session.id,
      isUpcoming: !session.hasEnded,
      positionInSeries: session.positionInSeries,
      startDate: DateTime.fromISO(session.startDate),
      endDate: DateTime.fromISO(session.endDate),
      title: session.displayTitle,
      topicName: session.topicName ?? '',
      description: session.description ?? EMPTY_RICH_TEXT_VALUE,
      attendancePolicy: session.attendancePolicy,
      excusedAbsenceSource,
      didOrWillAttend,
      didAttemptSession,
      attemptedBreakout: session.viewer.attemptedBreakout,
      reflections: session.reflections,
    }
  })

  const actionRequiredSessions = isDialogue
    ? allSessions.filter(session => {
        const isExpired =
          session.endDate < now.minus({ days: DIALOGUES_REFLECTION_EXPIRES_AFTER_DAYS })
        return (
          !session.isUpcoming &&
          session.attemptedBreakout &&
          session.reflections.length === 0 &&
          !isExpired
        )
      })
    : []

  const handleOpenReflectionModal = async (sessionId: number) => {
    await router.push(
      updateQueryParams({
        params: [{ key: QUERY_REFLECTION_SESSION_ID, value: String(sessionId) }],
        router,
      })
    )
  }

  const handleCloseReflectionModal = async () => {
    await router.replace(
      updateQueryParams({
        params: [{ key: QUERY_REFLECTION_SESSION_ID, value: null }],
        router,
      })
    )
  }

  const [currentTab, tabToUrl] = useUrlTabController<ScheduleTabs>({
    queryKey: 'show',
    tabToQueryParam: {
      Upcoming: 'upcoming',
      'Action Required': 'action',
      All: 'all',
    },
    defaultTab: isDialogue && actionRequiredSessions.length > 0 ? 'Action Required' : 'Upcoming',
  })

  const [excusedAbsenceModalSessionId, setExcusedAbsenceModalSessionId] = useState<number | null>(
    null
  )

  const sessions = seriesHasEnded
    ? allSessions
    : currentTab === 'Upcoming'
      ? allSessions.filter(s => s.isUpcoming)
      : currentTab === 'Action Required'
        ? actionRequiredSessions
        : allSessions

  const attendancePolicy = series.attendancePolicy
  const hasRequiredAttendanceSessions = series.hasRequiredAttendanceSessions

  const isSATSeries = series.isSATSeries

  const attendancePolicyComponent = !isUserParticipant ? (
    <SeriesAttendancePolicy
      attendancePolicy={attendancePolicy}
      hasRequiredAttendanceSessions={hasRequiredAttendanceSessions}
      isSATBootcamp={isSATSeries}
    />
  ) : null

  return (
    <>
      <DialoguesReflectionModal
        isOpen={selectedSessionId != null}
        onClose={handleCloseReflectionModal}
      />
      <ExcusedAbsenceModal
        upcomingSessions={sessions.filter(({ isUpcoming }) => isUpcoming)}
        isOpen={excusedAbsenceModalSessionId != null}
        onClose={() => setExcusedAbsenceModalSessionId(null)}
        preSelectedSessionId={excusedAbsenceModalSessionId}
      />
      <HStack alignItems="center" justifyContent="space-between">
        <Heading size="xl">Sessions</Heading>
        {seriesHasEnded ? null : (
          <Tabs<ScheduleTabs>
            currentTab={currentTab}
            tabs={
              isDialogue && isUserParticipant
                ? ['Action Required', 'Upcoming', 'All']
                : ['Upcoming', 'All']
            }
            tabToUrl={tabToUrl}
            hideBorder
          />
        )}
      </HStack>
      {attendancePolicyComponent}
      {sessions.length > 0 ? (
        <SessionCardList
          sessions={sessions}
          isUserRegisteredLearner={isUserRegisteredLearner}
          openExcusedAbsenceModal={(sessionId?: number) =>
            setExcusedAbsenceModalSessionId(sessionId ?? null)
          }
          openReflectionModal={async (sessionId: number) => {
            await handleOpenReflectionModal(sessionId)
          }}
          isDialogue={isDialogue}
        />
      ) : (
        <Text>No sessions found.</Text>
      )}
    </>
  )
}
